import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Col, Row } from "reactstrap";
import UploadCertificate from "./UploadCertificate";

function isValidUrl(url) {
  // Accepts www.example.com, http(s)://www.example.com, etc.
  return /^(https?:\/\/)?(www\.)[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/.*)?$/.test(url);
}

const degreeOptions = [
    "Bachelor's or equivalent",
    "Master's",
    "Ph.D.",
    "Post-doctorate"
];
const years = Array.from({ length: 70 }, (_, i) => 2024 - i);

const EducationalBackground = forwardRef(({ applicationId, onNext, onSave, data, onDataChange, resumeData }, ref) => {

    console.log("Received resumeData in EducationalBackground:", resumeData, data);
    const [formData, setFormData] = useState({
        degree: "",
        fieldOfStudy: "",
        graduationYear: "",
        institutionName: "",
        city: "",
        state: "",
        country: "",
        webpage: "",
        file: null
    });
    const [errors, setErrors] = useState({});
    const [showValidation, setShowValidation] = useState(false);
    useEffect(() => {
        if (data && JSON.stringify(data) !== JSON.stringify(formData)) {
            setFormData(prev => ({
                ...prev,
                ...data
            }));
        }
        // eslint-disable-next-line
    }, [data]);

    // Populate from resumeData if available
    useEffect(() => {
        if (resumeData?.education?.[0]) {
            const edu = resumeData.education[0];
            // console.log("Populating educational background from resumeData:", edu);
            setFormData(prev => ({
                ...prev,
                degree: prev.degree,
                fieldOfStudy: edu.fieldOfStudy || prev.fieldOfStudy || "",
                graduationYear: edu.graduationYear || prev.graduationYear || "",
                institutionName: edu.institution || prev.institutionName || "",
                city: edu.city || prev.city || "",
                state: edu.state || prev.state || "",
                country: edu.country || prev.country || "",
                webpage: edu.webpage || prev.webpage || ""
            }));
        }
    }, [resumeData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const sanitizedValue = value === null ? "" : value;
        setFormData(prev => {
            const updated = { ...prev, [name]: sanitizedValue };
            // console.log('Updated education formData:', updated);
            if (onDataChange) onDataChange(updated);
            return updated;
        });
    };

    const handleFileSelect = (file) => {
        setFormData(prev => {
            const updated = { ...prev, file };
            // console.log('Updated education formData (file):', updated);
            if (onDataChange) onDataChange(updated);
            return updated;
        });
    };

    // Validation function
    const validate = () => {
        const newErrors = {};
        if (!formData.degree) newErrors.degree = "Degree is required";
        if (!formData.fieldOfStudy) newErrors.fieldOfStudy = "Field of Study is required";
        if (!formData.graduationYear) newErrors.graduationYear = "Graduation Year is required";
        if (!formData.institutionName) newErrors.institutionName = "Institution Name is required";
        if (!formData.city) newErrors.city="City is required";
        if (!formData.state) newErrors.state="State is required";
        if (!formData.country) newErrors.country="Country is required";
        // Webpage URL validation
        if (formData.webpage && !isValidUrl(formData.webpage)) {
            newErrors.webpage = "Please enter a valid webpage in format www.example.com";
        }
        if (!formData.webpage) {
            newErrors.webpage = "Webpage is required";
        }
        if (!formData.file) {
            newErrors.file = "Degree is required";
        }
        setErrors(newErrors);
        setShowValidation(true);
        return Object.keys(newErrors).length === 0;
    };

    useImperativeHandle(ref, () => ({
        validate,
        getFormData: () => formData
    }));

    return (
        <>
            <Row>
                <Col xl="12">
                    <h2 className="personalinfo_head">Educational Background</h2>
                </Col>
            </Row>
            <Row>
                <Col xl="4">
                    <div className="personal_formdetail">
                        <h5>Highest Degree Obtained <span>*</span></h5>
                        <select
                            className={`select_gender${showValidation && errors.degree ? ' is-invalid' : ''}`}
                            name="degree"
                            value={formData.degree}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select degree</option>
                            {degreeOptions.map((deg) => (
                                <option key={deg} value={deg}>{deg}</option>
                            ))}
                        </select>
                        {showValidation && errors.degree && <div className="invalid-feedback d-block">{errors.degree}</div>}
                    </div>
                </Col>
                <Col xl="4">
                    <div className="personal_formdetail">
                        <h5>Field of Study <span>*</span></h5>
                        <input
                            type="text"
                            className={`form-control form_custom${showValidation && errors.fieldOfStudy ? ' is-invalid' : ''}`}
                            placeholder="Field of Study"
                            name="fieldOfStudy"
                            value={formData.fieldOfStudy}
                            onChange={handleChange}
                            required
                        />
                        {showValidation && errors.fieldOfStudy && <div className="invalid-feedback d-block">{errors.fieldOfStudy}</div>}
                    </div>
                </Col>
                <Col xl="4">
                    <div className="personal_formdetail">
                        <h5>Graduation Year <span>*</span></h5>
                        <select
                            className={`select_gender${showValidation && errors.graduationYear ? ' is-invalid' : ''}`}
                            name="graduationYear"
                            value={formData.graduationYear}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Year</option>
                            {years.map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                        {showValidation && errors.graduationYear && <div className="invalid-feedback d-block">{errors.graduationYear}</div>}
                    </div>
                </Col>
            </Row>
            <Row>

                <Col xl="4">
                    <div className="personal_formdetail">
                        <h5>Institution <span>*</span></h5>
                        <input
                            type="text"
                            className={`form-control form_custom${showValidation && errors.institutionName ? ' is-invalid' : ''}`}
                            placeholder="Name"
                            name="institutionName"
                            value={formData.institutionName}
                            onChange={handleChange}
                            required
                        />
                        {showValidation && errors.institutionName && <div className="invalid-feedback d-block">{errors.institutionName}</div>}
                    </div>
                </Col>
                <Col xl="4">
                    <div className="personal_formdetail">
                        <h5>&nbsp;</h5>
                        <input
                            type="text"
                            className='form-control form_custom'
                            placeholder="City/District"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            required
                        />
                        {showValidation && errors.city && <div className="invalid-feedback d-block">{errors.city}</div>}
                    </div>
                </Col>
                <Col xl="4">
                    <div className="personal_formdetail">
                        <h5>&nbsp;</h5>
                        <input
                            type="text"
                            className='form-control form_custom'
                            placeholder="State/Province"
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            required
                        />
                        {showValidation && errors.state && <div className="invalid-feedback d-block">{errors.state}</div>}
                    </div>
                </Col>

            </Row>
            <Row>
                <Col xl="4">
                    <div className="personal_formdetail">

                        <input
                            type="text"
                            className='form-control form_custom'
                            placeholder="Country"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            required
                        />
                        {showValidation && errors.country && <div className="invalid-feedback d-block">{errors.country}</div>}
                    </div>
                </Col>
            </Row>
            <Row>
                <Col xl="4">
                    <div className="personal_formdetail">
                        <h5>Webpage </h5>
                        <input
                            type="url"
                            className={`form-control form_cap form_custom${showValidation && errors.webpage ? ' is-invalid' : ''}`}
                            placeholder="Please enter Webpage link"
                            name="webpage"
                            required
                            value={formData.webpage}
                            onChange={handleChange}
                        />
                        {showValidation && errors.webpage && <div className="invalid-feedback d-block">{errors.webpage}</div>}
                    </div>
                </Col>
            </Row>
            <Row>
                <Col xl="12">
                    <UploadCertificate
                        onFileSelect={handleFileSelect}
                        initialFile={formData.file}
                    />
                    {showValidation && errors.file && <div className="invalid-feedback d-block">{errors.file}</div>}

                </Col>
            </Row>
        </>
    );
});

export default EducationalBackground;
